import { Drawer, Paragraph, Select } from '@tmov/design-system';
import { Form, notification } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { carregaCidades } from '../../../../helpers/cidades';
import { toTitleCase } from '../../../../helpers/formatador';
import { searchProdutosAts } from '../../../../helpers/produtos';
import { DrawerFooter } from './DrawerFooter';
import { DrawerHeader } from './DrawerHeader';

function FiltersDrawer({
  drawerActive,
  filters,
  setDrawerActive,
  updateFilters,
}) {
  const [form] = Form.useForm();

  const [produtoSearch, setProdutoSearch] = useState('');
  const [produtoValues, setProdutoValues] = useState([]);
  const [produtoLoading, setProdutoLoading] = useState(false);
  const produtoOptions = useMemo(
    () =>
      produtoValues?.length > 0
        ? produtoValues.map(item => ({
            id: item.IdProduto,
            value: toTitleCase(item.Descricao),
          }))
        : [],
    [produtoValues]
  );
  const handleChangeProdutoSearch = useDebouncedCallback(searchValue => {
    setProdutoSearch(searchValue);
  }, 300);

  const [origemSearch, setOrigemSearch] = useState('');
  const [origemValues, setOrigemValues] = useState([]);
  const [origemLoading, setOrigemLoading] = useState(false);
  const origemOptions = useMemo(
    () =>
      origemValues?.length > 0
        ? origemValues.map(item => ({
            id: item.idCidade,
            value: `${toTitleCase(item.cidadeNome)} - ${item.sigla}`,
          }))
        : [],
    [origemValues]
  );
  const handleChangeOrigemSearch = useDebouncedCallback(searchValue => {
    setOrigemSearch(searchValue);
  }, 300);

  const [destinoSearch, setDestinoSearch] = useState('');
  const [destinoValues, setDestinoValues] = useState([]);
  const [destinoLoading, setDestinoLoading] = useState(false);
  const destinoOptions = useMemo(
    () =>
      destinoValues?.length > 0
        ? destinoValues.map(item => ({
            id: item.idCidade,
            value: `${toTitleCase(item.cidadeNome)} - ${item.sigla}`,
          }))
        : [],
    [destinoValues]
  );
  const handleChangeDestinoSearch = useDebouncedCallback(searchValue => {
    setDestinoSearch(searchValue);
  }, 300);

  const clearFilters = () => {
    form.resetFields();
    updateFilters({
      produto: null,
      origem: null,
      destino: null,
    });
  };

  const onSubmit = values => {
    updateFilters({ ...values });
    setDrawerActive(false);
  };

  const onFinishFailed = errorInfo => {
    notification.error({
      message: 'Ocorreu um erro ao aplicar os valores do filtro',
      description: errorInfo,
    });
  };

  useEffect(() => {
    const loadProdutoValues = async () => {
      await searchProdutosAts(
        produtoSearch,
        setProdutoValues,
        setProdutoLoading
      );
    };
    loadProdutoValues();
  }, [produtoSearch]);

  useEffect(() => {
    const loadRotasOrigem = async () => {
      await carregaCidades(origemSearch, setOrigemValues, setOrigemLoading);
    };
    loadRotasOrigem();
  }, [origemSearch]);

  useEffect(() => {
    const loadRotasDestino = async () => {
      await carregaCidades(destinoSearch, setDestinoValues, setDestinoLoading);
    };
    loadRotasDestino();
  }, [destinoSearch]);

  return (
    <Drawer
      header={<DrawerHeader clearFilters={clearFilters} />}
      footer={<DrawerFooter submitForm={form.submit} />}
      active={drawerActive}
      updateActive={setDrawerActive}
    >
      <Form form={form} onFinish={onSubmit} onFinishFailed={onFinishFailed}>
        <Paragraph
          variant="p2"
          fontWeight="semibold"
          style={{ marginBottom: '4px' }}
        >
          Produto
        </Paragraph>
        <Paragraph variant="p2">Filtre por um produto específico.</Paragraph>
        <Form.Item
          name="produto"
          valuePropName="selectedOption"
          initialValue={filters.produto}
        >
          <Select
            id="produto"
            label="Produto"
            onChangeSearch={handleChangeProdutoSearch}
            options={produtoOptions}
            isLoading={produtoLoading}
            width="320px"
            data-testid="produto-select"
            noResultsText={
              produtoSearch.length < 3
                ? 'Insira pelo menos 3 caracteres para pesquisar'
                : 'Não há itens que correspondem à pesquisa'
            }
          />
        </Form.Item>
        <br />
        <Paragraph
          variant="p2"
          fontWeight="semibold"
          style={{ marginBottom: '4px' }}
        >
          Rota
        </Paragraph>
        <Paragraph variant="p2">
          Filtre por origem e/ou destino da viagem.
        </Paragraph>
        <Form.Item
          name="origem"
          valuePropName="selectedOption"
          initialValue={filters.origem}
        >
          <Select
            id="origem"
            label="Rota de origem"
            onChangeSearch={handleChangeOrigemSearch}
            options={origemOptions}
            isLoading={origemLoading}
            width="320px"
            data-testid="rota-origem-select"
            noResultsText={
              origemSearch.length < 3
                ? 'Insira pelo menos 3 caracteres para pesquisar'
                : 'Não há itens que correspondem à pesquisa'
            }
          />
        </Form.Item>
        <Form.Item name="destino" initialValue={filters.destino}>
          <Select
            id="destino"
            label="Rota de destino"
            valuePropName="selectedOption"
            onChangeSearch={handleChangeDestinoSearch}
            options={destinoOptions}
            isLoading={destinoLoading}
            width="320px"
            data-testid="rota-destino-select"
            noResultsText={
              destinoSearch.length < 3
                ? 'Insira pelo menos 3 caracteres para pesquisar'
                : 'Não há itens que correspondem à pesquisa'
            }
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

const filter = PropTypes.shape({
  id: PropTypes.number,
  value: PropTypes.string,
});

FiltersDrawer.propTypes = {
  drawerActive: PropTypes.bool.isRequired,
  filters: PropTypes.shape({
    produto: filter,
    origem: filter,
    destino: filter,
  }).isRequired,
  setDrawerActive: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
};

export default FiltersDrawer;
